/* eslint-disable no-console */
import { useCallback } from "react";
import { Call } from "@twilio/voice-sdk";
import { extractPhoneFromClientId, formatName } from "@tellescope/utils";
import { useCallingStore } from "../../store";
import { useAuth } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";

// Options for making an outbound call
export interface MakeCallOptions {
  enduserId?: string;
  enduserName?: string;
  fromNumber?: string;
  conferenceId?: string;
  recordConference?: boolean;
  ticketId?: string;
  note?: string;
}

/**
 * Hook for call control operations.
 *
 * Responsibilities:
 * - Make outbound calls
 * - Answer incoming calls
 * - Reject/ignore incoming calls
 * - Hang up active call
 * - Toggle mute
 * - Send DTMF digits
 */
export function useCallOperations() {
  const { t } = useTranslation("calling");
  const { session } = useAuth();
  const callingStore = useCallingStore();

  // Get store actions directly to avoid stale closures
  const getCallingActions = useCallback(() => {
    return {
      setActiveCall: callingStore.setActiveCall,
      setCallStatus: callingStore.setCallStatus,
      setCallDirection: callingStore.setCallDirection,
      setMuted: callingStore.setMuted,
      setConference: callingStore.setConference,
      setActiveCallMeta: callingStore.setActiveCallMeta,
      clearActiveCallMeta: callingStore.clearActiveCallMeta,
      removeIncomingCall: callingStore.removeIncomingCall,
      resetCallState: callingStore.resetCallState,
    };
  }, [
    callingStore.clearActiveCallMeta,
    callingStore.removeIncomingCall,
    callingStore.resetCallState,
    callingStore.setActiveCall,
    callingStore.setActiveCallMeta,
    callingStore.setCallDirection,
    callingStore.setCallStatus,
    callingStore.setConference,
    callingStore.setMuted,
  ]);

  /**
   * Sets up event listeners for a new call
   */
  const setupCallEventListeners = useCallback(
    (call: Call) => {
      const actions = getCallingActions();

      call.on("accept", () => {
        console.log("[Calling:Ops] Call accepted (audio connected)");
        actions.setCallStatus("open");
      });

      call.on("disconnect", () => {
        console.log("[Calling:Ops] Call disconnected");
        actions.setCallStatus("disconnected");
        // Reset call state after a brief delay to allow UI to show disconnected state
        setTimeout(() => {
          actions.resetCallState();
        }, 1000);
      });

      call.on("cancel", () => {
        console.log("[Calling:Ops] Call cancelled");
        actions.resetCallState();
      });

      call.on("error", (error) => {
        console.error("[Calling:Ops] Call error:", error);
        actions.setCallStatus("disconnected");
      });

      call.on("ringing", () => {
        console.log("[Calling:Ops] Call ringing");
        actions.setCallStatus("ringing");
      });
    },
    [getCallingActions]
  );

  /**
   * Makes an outbound call to a phone number
   */
  const makeCall = useCallback(
    async (to: string, options: MakeCallOptions = {}): Promise<Call | null> => {
      const device = callingStore.device;
      if (!device) {
        console.error("[Calling:Ops] No device available for calling");
        return null;
      }

      const actions = getCallingActions();
      actions.setCallStatus("connecting");
      actions.setCallDirection("outbound");

      // Set call metadata for UI
      actions.setActiveCallMeta({
        enduserId: options.enduserId ?? null,
        enduserName: options.enduserName ?? null,
        phoneNumber: to,
        orgNumber: options.fromNumber ?? null,
        startTime: new Date(),
      });

      try {
        // Connect via Twilio Device
        const call = await device.connect({
          params: {
            To: to,
            ...(options.enduserId && { enduserId: options.enduserId }),
            ...(options.fromNumber && { FromNumber: options.fromNumber }),
            ...(options.conferenceId && { ConferenceId: options.conferenceId }),
            ...(options.recordConference !== undefined && {
              RecordConference: String(options.recordConference),
            }),
            ...(options.ticketId && { ticketId: options.ticketId }),
            ...(options.note && { note: options.note }),
          },
        });

        setupCallEventListeners(call);
        actions.setActiveCall(call);

        // Set conference state if provided
        if (options.conferenceId) {
          actions.setConference({
            id: options.conferenceId,
            users: [],
            phones: [],
            enduserName: options.enduserName ?? "",
          });
        }

        console.log("[Calling:Ops] Outbound call initiated");
        return call;
      } catch (error) {
        console.error("[Calling:Ops] Failed to make call:", error);
        actions.resetCallState();
        return null;
      }
    },
    [callingStore.device, getCallingActions, setupCallEventListeners]
  );

  /**
   * Answers an incoming call
   */
  const answerCall = useCallback(
    async (incomingCall: Call): Promise<void> => {
      const device = callingStore.device;
      if (!device) {
        console.error("[Calling:Ops] No device available");
        return;
      }

      const actions = getCallingActions();

      // Disconnect any existing calls
      device.disconnectAll();

      // Accept the incoming call
      incomingCall.accept();

      // Remove from incoming queue
      actions.removeIncomingCall(incomingCall);

      // Set call state
      actions.setActiveCall(incomingCall);
      actions.setCallDirection("inbound");
      actions.setCallStatus("connecting");

      // Extract metadata from custom parameters
      const enduserId = incomingCall.customParameters?.get("eId") ?? null;
      const enduserName = incomingCall.customParameters?.get("eName") ?? null;
      const conferenceId = incomingCall.customParameters?.get("cId") ?? null;

      // Extract phone number from Twilio client identifier if present
      // Format: "client:+14156923468-userId-orgId" -> "+14156923468"
      const rawOrgNumber = incomingCall.parameters?.To ?? null;
      const orgNumber = rawOrgNumber ? extractPhoneFromClientId(rawOrgNumber) : null;

      actions.setActiveCallMeta({
        enduserId,
        enduserName,
        phoneNumber: incomingCall.parameters?.From ?? null,
        orgNumber,
        startTime: new Date(),
      });

      // Set conference state if present
      if (conferenceId) {
        actions.setConference({
          id: conferenceId,
          users: [],
          phones: [],
          enduserName: enduserName ?? "",
        });
      }

      setupCallEventListeners(incomingCall);

      // If no enduser ID from custom params (e.g. queue calls), look up by phone number
      if (!enduserId && session && incomingCall.parameters?.From) {
        try {
          const endusers = await session.api.endusers.getSome({
            filter: { phone: incomingCall.parameters.From },
            limit: 1,
          });
          const enduser = endusers[0];
          if (enduser) {
            actions.setActiveCallMeta({
              enduserId: enduser.id,
              enduserName: formatName(enduser) || enduser.phone || null,
            });
          }
        } catch (error) {
          console.error("[Calling:Ops] Failed to look up enduser by phone:", error);
        }
      }

      // Update the phone call record in database to mark as answered
      if (session && incomingCall.parameters?.From) {
        try {
          const calls = await session.api.phone_calls.getSome({
            filter: {
              from: incomingCall.parameters.From,
              inbound: true,
            },
            limit: 1,
            sort: "newFirst",
          });

          const call = calls[0];
          if (call && !call.userId) {
            await session.api.phone_calls.updateOne(call.id, {
              userId: session.userInfo?.id,
            });
          }
        } catch (error) {
          console.error("[Calling:Ops] Failed to update call record:", error);
        }
      }

      console.log("[Calling:Ops] Answered incoming call");
    },
    [callingStore.device, getCallingActions, session, setupCallEventListeners]
  );

  /**
   * Rejects an incoming call (sends to voicemail)
   */
  const rejectCall = useCallback(
    (incomingCall: Call): void => {
      const actions = getCallingActions();
      callingStore.stopIncomingRing();
      incomingCall.reject();
      actions.removeIncomingCall(incomingCall);
      console.log("[Calling:Ops] Rejected incoming call");
    },
    [callingStore, getCallingActions]
  );

  /**
   * Ignores an incoming call (silent dismiss, keeps ringing on other devices)
   */
  const ignoreCall = useCallback(
    (incomingCall: Call): void => {
      const actions = getCallingActions();
      callingStore.stopIncomingRing();
      incomingCall.ignore();
      actions.removeIncomingCall(incomingCall);
      console.log("[Calling:Ops] Ignored incoming call");
    },
    [callingStore, getCallingActions]
  );

  /**
   * Hangs up the active call
   */
  const hangUp = useCallback((): void => {
    const activeCall = callingStore.activeCall;
    if (activeCall) {
      activeCall.disconnect();
      console.log("[Calling:Ops] Hung up active call");
    }

    // Also disconnect all calls via device as fallback
    const device = callingStore.device;
    device?.disconnectAll();
  }, [callingStore.activeCall, callingStore.device]);

  /**
   * Toggles mute on the active call
   */
  const toggleMute = useCallback((): void => {
    const activeCall = callingStore.activeCall;
    if (!activeCall) {
      console.warn("[Calling:Ops] No active call to mute/unmute");
      return;
    }

    // Can only mute when call is open
    if (activeCall.status() !== "open") {
      console.warn("[Calling:Ops] Call not open, cannot toggle mute");
      return;
    }

    const isMuted = activeCall.isMuted();
    activeCall.mute(!isMuted);

    const actions = getCallingActions();
    actions.setMuted(!isMuted);

    console.log("[Calling:Ops] Mute toggled:", !isMuted);
  }, [callingStore.activeCall, getCallingActions]);

  /**
   * Sends DTMF digits (keypad tones) on the active call
   */
  const sendDigits = useCallback(
    (digits: string): void => {
      const activeCall = callingStore.activeCall;
      if (!activeCall) {
        console.warn("[Calling:Ops] No active call for DTMF");
        return;
      }

      // Validate digits (0-9, *, #, w for wait)
      const validDigits = digits.replace(/[^0-9#*w]/gi, "");
      if (validDigits.length === 0) {
        console.warn("[Calling:Ops] No valid DTMF digits to send");
        return;
      }

      activeCall.sendDigits(validDigits);
      console.log("[Calling:Ops] Sent DTMF digits:", validDigits);
    },
    [callingStore.activeCall]
  );

  /**
   * Gets the current call status as a string
   */
  const getCallStatusLabel = useCallback((): string => {
    const activeCall = callingStore.activeCall;
    if (!activeCall) return "No active call";

    const status = activeCall.status();
    switch (status) {
      case "pending":
        return t("Connecting...");
      case "connecting":
        return t("Connecting...");
      case "ringing":
        return t("Ringing...");
      case "open":
        return t("Connected");
      case "closed":
        return t("Call ended");
      default:
        return status;
    }
  }, [callingStore.activeCall, t]);

  return {
    // State
    activeCall: callingStore.activeCall,
    callStatus: callingStore.callStatus,
    callDirection: callingStore.callDirection,
    isMuted: callingStore.isMuted,
    incomingCalls: callingStore.incomingCalls,
    activeCallMeta: callingStore.activeCallMeta,

    // Call actions
    makeCall,
    answerCall,
    rejectCall,
    ignoreCall,
    hangUp,

    // Call control
    toggleMute,
    sendDigits,

    // Utilities
    getCallStatusLabel,
  };
}
